import { MOBILE_VIEW_THRESHOLD_PX, ROUTE_HOMEPAGE, ROUTE_LANDING } from '@1bill-app/constants';
import { APP_CONFIG, EXPLANATION_NOTES_SIMPLE } from '@1bill-app/constants';
import { environment } from '@1bill-app/env';
import { objDeepEquals } from '@1bill-app/helpers/object.helper';
import { PushNotificationsService } from '@1bill-app/services/push-notifications.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import {
  createInitialState,
  NotifySubscribeTopic,
  Settings,
  SettingsOptions,
} from '@1bill-app/services/settings/settings.store';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  AlertController,
  LoadingController,
  Platform,
  ToastController,
  PopoverController,
  NavController,
} from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';
import { PopoverSimpleComponent } from '@1bill-app/components/popover/popover-simple/popover-simple.component';
import { NotificationChannel } from '@1bill-app/services/notification/notification.type';

@Component({
  selector: 'app-allow-notifications',
  templateUrl: './allow-notifications.page.html',
  styleUrls: ['./allow-notifications.page.scss'],
})
export class AllowNotificationsPage implements OnInit {
  constructor(
    private platform: Platform,
    public alertCtrl: AlertController,
    private _settings: SettingsService,
    public query: SettingsQuery,
    private navCtrl: NavController,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private logger: NGXLogger,
    private pushService: PushNotificationsService,
  ) {
    this.pushService.subscribe();
  }

  subs = new SubSink();
  loading: any;
  saving: boolean = false;

  landingPage = ROUTE_LANDING;

  get isDesktopView() {
    return this.platform.width() >= MOBILE_VIEW_THRESHOLD_PX;
  }

  ngOnInit() {
    this.fetchData();
    this.query.select().subscribe({
      next: (settingsData) => {
        // this.logger.log('query fetch settings data: ' + JSON.stringify(settingsData))
      },
      error: (err) => {
        this.logger.error('unable to retrieve user setting from data store', err);
      },
    });
  }

  async fetchData() {
    this.loading = await this.loadingController.create({
      message: 'Loading settings data...',
    });
    this.loading.present();
    this.subs.sink = this._settings.fetch().subscribe({
      next: (r) => {
        // this.logger.log('user settings data fetched:', JSON.stringify(r));
        this.loading.dismiss();
      },
      error: (e) => {
        this.logger.error('unable to fetch user settings data', e);
        this.loading.dismiss();
      },
    });
  }

  async showAlert() {
    const alert = await this.alertCtrl.create({
      mode: 'ios',
      header: '“1Bill” Would Like to Send You Notifications',
      message: 'Notifications may include alerts, sounds and icon badges.',
      buttons: [
        {
          text: "Don't Allow",
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            this.onSave(false);
          },
        },
        {
          text: 'Allow',
          handler: () => {
            this.onSave(true);
          },
        },
      ],
    });
    await alert.present();
    const result = await alert.onDidDismiss();
    console.log(result);
  }

  async onSave(allowing: boolean) {
    this.loading = await this.loadingController.create({
      message: 'Saving notification settings...',
    });
    this.loading.present();
    try {
      // STAGE: ready for save
      this.logger.debug('Allowing Notifications:', allowing);
      this.saving = true;
      let data: Settings = createInitialState();
      data.notifyMethod = [];
      data.notifySubscribeTopics = [];
      data.reminderDays = [];

      //Device Notifications only true if user clicks "Allow" on alert.
      if (allowing) data.notifyMethod.push(NotificationChannel.DEVICE);

      //Email and SMS set to true by default
      data.notifyMethod.push(NotificationChannel.EMAIL);
      data.notifyMethod.push(NotificationChannel.SMS);

      //All Notification Topics set to true by default
      Object.values(NotifySubscribeTopic).forEach((topic) => {
        data.notifySubscribeTopics.push(topic);
      });

      //All Reminder Days set to true by default
      const reminderDays = [0, 1, 2, 7];
      reminderDays.forEach((day) => {
        data.reminderDays.push(day);
      });

      // Save notification
      const notificationPayload: SettingsOptions = {
        option: 'NOTIFICATION',
        settingsData: data,
      };
      await this._settings.saveSettingDetails(notificationPayload).toPromise();
      // Save reminder
      const reminderPayload: SettingsOptions = { option: 'REMINDER', settingsData: data };
      await this._settings.saveSettingDetails(reminderPayload).toPromise();
      this.loading.dismiss();
      this.showSuccess('Settings saved successfully.');
      this.fetchData();
    } catch (err) {
      this.logger.error('onSave', err);
      // display error toast
      this.showError('Error occured while saving settings data!!');
      this.loading.dismiss();
    }
    this.saving = false;

    this.navCtrl.navigateRoot(ROUTE_HOMEPAGE);
  }

  async showSuccess(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: APP_CONFIG.TOAST_SUCCESS_DURATION,
      cssClass: ['toast-error'],
    });
    await toast.present();
  }

  async showError(err: any, danger = false) {
    if (!err) {
      return;
    }
    const message = err.statusText || err.message || err;
    const toast = await this.toastController.create({
      message,
      duration: APP_CONFIG.TOAST_ERROR_DURATION,
      color: danger ? 'danger' : 'warning',
      // cssClass: ['toast-error']
    });
    await toast.present();
  }
}
